/**
 * UnifiedScenarioTable - Cross-app scenario matrix
 *
 * Standardized table for displaying scenarios across Site DNA and Flux EV.
 * Builds on ProjectTable primitives. Supports compare checkboxes, expandable
 * rows, configurable columns, and retro/light theme modes.
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Checkbox,
    Collapse,
    CircularProgress,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../theme-mode/useThemeMode.js';
import { vortxColors, vortxTypography } from '../core/theme.js';
import { retroColors } from '../core/theme.js';
import ScenarioStatusChip from './ScenarioStatusChip.jsx';

/**
 * Format a relative date string (e.g., "3d ago")
 */
function formatRelativeDate(date) {
    if (!date) return '\u2014';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '\u2014';

    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
}

/**
 * Header cell with consistent scenario sub-table styling.
 * Uses native <th> to avoid MUI TableCell context warnings inside native <table>.
 */
function ScenarioHeaderCell({ children, isRetro, textSecondary, style = {} }) {
    return (
        <th
            style={{
                color: isRetro ? '#5fd4c4' : (textSecondary || 'inherit'),
                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                fontSize: isRetro ? '0.65rem' : '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                borderBottom: isRetro
                    ? '1px solid rgba(95, 212, 196, 0.3)'
                    : `1px solid rgba(63, 106, 100, 0.3)`,
                padding: '8px',
                textAlign: 'left',
                ...style,
            }}
        >
            {children}
        </th>
    );
}

export default function UnifiedScenarioTable({
    scenarios = [],

    // Column renderers (app-specific)
    formatConfig,
    getHasResults,
    getStatusLabel,
    getCreatedDate,

    // Row click
    onRowClick,

    // Expandable rows
    expandable = false,
    renderExpanded,

    // Compare
    compareEnabled = false,
    selectedForCompare,
    onCompareToggle,
    onCompare,

    // Actions
    actions = [],

    // Create new
    onCreateScenario,
    createLabel = 'New Scenario',

    // Loading state
    isLoading = false,
    emptyMessage = 'No scenarios yet',
}) {
    const { isRetro, isDark } = useThemeMode();
    const [expandedRows, setExpandedRows] = useState(new Set());

    const textSecondary = isRetro ? '#888' : undefined;

    const toggleExpand = (scenarioId, e) => {
        e.stopPropagation();
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(scenarioId)) {
                next.delete(scenarioId);
            } else {
                next.add(scenarioId);
            }
            return next;
        });
    };

    const resolveHasResults = (scenario) => {
        if (getHasResults) return getHasResults(scenario);
        return false;
    };

    const resolveStatusLabel = (scenario, hasResults) => {
        if (getStatusLabel) return getStatusLabel(scenario, hasResults);
        return hasResults ? 'Completed' : 'Draft';
    };

    const resolveCreatedDate = (scenario) => {
        if (getCreatedDate) return getCreatedDate(scenario);
        return scenario.created_at;
    };

    const selectedCount = selectedForCompare?.size || 0;

    // Loading skeleton
    if (isLoading) {
        return (
            <Box sx={{ py: 2 }}>
                {[1, 2, 3].map(i => (
                    <Box
                        key={i}
                        className="vx-skeleton vx-slide-in-up"
                        sx={{ height: 40, borderRadius: isRetro ? 0 : 1, mb: 1 }}
                    />
                ))}
            </Box>
        );
    }

    // Empty state
    if (scenarios.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{
                    color: isRetro ? retroColors.muted : 'text.secondary',
                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                    fontSize: isRetro ? '0.8rem' : '0.9rem',
                    mb: 2,
                }}>
                    {isRetro ? `> ${emptyMessage.toLowerCase()}` : emptyMessage}
                </Typography>
                {onCreateScenario && (
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onCreateScenario}
                        sx={isRetro ? {
                            color: '#5fd4c4',
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            borderRadius: 0,
                            border: '1px solid rgba(95, 212, 196, 0.3)',
                            '&:hover': {
                                bgcolor: 'rgba(95, 212, 196, 0.1)',
                                borderColor: '#5fd4c4',
                            }
                        } : {
                            color: vortxColors.gold,
                            fontFamily: vortxTypography.body,
                            textTransform: 'none',
                            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) }
                        }}
                    >
                        {createLabel}
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {compareEnabled && (
                            <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary} style={{ width: 40, padding: '8px 4px' }}>
                            </ScenarioHeaderCell>
                        )}
                        {expandable && (
                            <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary} style={{ width: 40, padding: '8px 4px' }}>
                            </ScenarioHeaderCell>
                        )}
                        <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary}>
                            Scenario
                        </ScenarioHeaderCell>
                        <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary}>
                            Status
                        </ScenarioHeaderCell>
                        <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary}>
                            Config
                        </ScenarioHeaderCell>
                        <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary}>
                            Created
                        </ScenarioHeaderCell>
                        <ScenarioHeaderCell isRetro={isRetro} textSecondary={textSecondary} style={{ textAlign: 'right' }}>
                            Actions
                        </ScenarioHeaderCell>
                    </tr>
                </thead>
                <tbody>
                    {scenarios.map((scenario) => {
                        const hasResults = resolveHasResults(scenario);
                        const statusLabel = resolveStatusLabel(scenario, hasResults);
                        const createdDate = resolveCreatedDate(scenario);
                        const isRowExpanded = expandedRows.has(scenario.id);
                        const visibleActions = actions.filter(a => !a.show || a.show(scenario));

                        return (
                            <React.Fragment key={scenario.id}>
                                {/* Main scenario row */}
                                <tr
                                    onClick={(e) => onRowClick?.(scenario, e)}
                                    onAuxClick={(e) => {
                                        if (e.button === 1) {
                                            e.preventDefault();
                                            onRowClick?.(scenario, e);
                                        }
                                    }}
                                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                >
                                    {compareEnabled && (
                                        <td
                                            style={{
                                                width: 40,
                                                padding: '6px 8px',
                                                borderBottom: isRetro
                                                    ? '1px solid rgba(63, 106, 100, 0.2)'
                                                    : '1px solid rgba(63, 106, 100, 0.15)',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={selectedForCompare?.has(scenario.id) || false}
                                                onChange={() => onCompareToggle?.(scenario.id)}
                                                disabled={!hasResults}
                                                sx={isRetro ? {
                                                    color: retroColors.muted,
                                                    p: 0.5,
                                                    '&.Mui-checked': { color: '#5fd4c4' },
                                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' }
                                                } : {
                                                    color: vortxColors.gray,
                                                    p: 0.5,
                                                    '&.Mui-checked': { color: vortxColors.teal },
                                                }}
                                            />
                                        </td>
                                    )}
                                    {expandable && (
                                        <td
                                            style={{
                                                width: 40,
                                                padding: '6px 8px',
                                                borderBottom: isRetro
                                                    ? '1px solid rgba(63, 106, 100, 0.2)'
                                                    : '1px solid rgba(63, 106, 100, 0.15)',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpand(scenario.id, e);
                                            }}
                                        >
                                            <IconButton size="small" sx={{ color: isRetro ? '#5fd4c4' : vortxColors.gold, p: 0.5 }}>
                                                {isRowExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </IconButton>
                                        </td>
                                    )}
                                    <td style={{
                                        padding: '8px 16px 8px 8px',
                                        borderBottom: isRetro
                                            ? '1px solid rgba(63, 106, 100, 0.2)'
                                            : '1px solid rgba(63, 106, 100, 0.15)',
                                    }}>
                                        <Typography sx={isRetro ? {
                                            color: '#5fd4c4',
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.8rem',
                                            fontWeight: hasResults ? 600 : 400,
                                            '&:hover': { color: vortxColors.gold },
                                        } : {
                                            color: vortxColors.gold,
                                            fontFamily: vortxTypography.body,
                                            fontSize: '0.9rem',
                                            fontWeight: hasResults ? 600 : 400,
                                            '&:hover': { textDecoration: 'underline' },
                                        }}>
                                            {scenario.name}
                                        </Typography>
                                    </td>
                                    <td style={{
                                        padding: '8px',
                                        borderBottom: isRetro
                                            ? '1px solid rgba(63, 106, 100, 0.2)'
                                            : '1px solid rgba(63, 106, 100, 0.15)',
                                    }}>
                                        <ScenarioStatusChip hasResults={hasResults} label={statusLabel} />
                                    </td>
                                    <td style={{
                                        padding: '8px',
                                        borderBottom: isRetro
                                            ? '1px solid rgba(63, 106, 100, 0.2)'
                                            : '1px solid rgba(63, 106, 100, 0.15)',
                                    }}>
                                        <Typography sx={isRetro ? {
                                            color: '#888',
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                        } : {
                                            color: 'text.secondary',
                                            fontFamily: vortxTypography.body,
                                            fontSize: '0.8rem',
                                        }}>
                                            {formatConfig ? formatConfig(scenario) : '\u2014'}
                                        </Typography>
                                    </td>
                                    <td style={{
                                        padding: '8px',
                                        borderBottom: isRetro
                                            ? '1px solid rgba(63, 106, 100, 0.2)'
                                            : '1px solid rgba(63, 106, 100, 0.15)',
                                    }}>
                                        <Typography sx={isRetro ? {
                                            color: '#888',
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                        } : {
                                            color: 'text.secondary',
                                            fontFamily: vortxTypography.body,
                                            fontSize: '0.8rem',
                                        }}>
                                            {formatRelativeDate(createdDate)}
                                        </Typography>
                                    </td>
                                    <td
                                        style={{
                                            padding: '8px',
                                            textAlign: 'right',
                                            borderBottom: isRetro
                                                ? '1px solid rgba(63, 106, 100, 0.2)'
                                                : '1px solid rgba(63, 106, 100, 0.15)',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            {visibleActions.map((action) => {
                                                const isDisabled = action.disabled?.(scenario) || false;
                                                const isActionLoading = action.loading?.(scenario) || false;

                                                return (
                                                    <Tooltip key={action.id} title={action.label}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => action.onClick(scenario, e)}
                                                                disabled={isDisabled || isActionLoading}
                                                                sx={isRetro ? {
                                                                    color: isDisabled ? 'rgba(255,255,255,0.25)' : retroColors.muted,
                                                                    borderRadius: 0,
                                                                    '&:hover': {
                                                                        color: action.id === 'delete' ? '#e74c3c' : '#5fd4c4',
                                                                    }
                                                                } : {
                                                                    color: isDisabled ? 'text.disabled' : vortxColors.gray,
                                                                    '&:hover': {
                                                                        color: action.id === 'delete' ? '#ff6b6b' : vortxColors.teal,
                                                                        bgcolor: action.id === 'delete'
                                                                            ? alpha('#d32f2f', 0.1)
                                                                            : alpha(vortxColors.teal, 0.1),
                                                                    }
                                                                }}
                                                            >
                                                                {isActionLoading ? (
                                                                    <CircularProgress size={16} color="inherit" />
                                                                ) : (
                                                                    React.cloneElement(action.icon, { sx: { fontSize: 16 } })
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </td>
                                </tr>

                                {/* Expandable row content */}
                                {expandable && renderExpanded && isRowExpanded && (
                                    <tr>
                                        <td
                                            colSpan={
                                                2 + // expand toggle + scenario name columns are always present
                                                (compareEnabled ? 1 : 0) +
                                                4 // status + config + created + actions
                                            }
                                            style={{ padding: 0, border: 'none' }}
                                        >
                                            <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    bgcolor: isRetro ? 'rgba(0, 0, 0, 0.3)' : alpha(vortxColors.warmStone, 0.15),
                                                    borderBottom: isRetro
                                                        ? '1px solid rgba(63, 106, 100, 0.2)'
                                                        : `1px solid ${alpha(vortxColors.teal, 0.15)}`,
                                                }}>
                                                    {renderExpanded(scenario)}
                                                </Box>
                                            </Collapse>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>

            {/* Bottom actions: New Scenario + Compare button */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                {onCreateScenario && (
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onCreateScenario}
                        sx={isRetro ? {
                            color: '#5fd4c4',
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            borderRadius: 0,
                            border: '1px solid rgba(95, 212, 196, 0.3)',
                            '&:hover': {
                                bgcolor: 'rgba(95, 212, 196, 0.1)',
                                borderColor: '#5fd4c4',
                            }
                        } : {
                            color: vortxColors.gold,
                            fontFamily: vortxTypography.body,
                            textTransform: 'none',
                            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) }
                        }}
                    >
                        {createLabel}
                    </Button>
                )}
                {compareEnabled && selectedCount >= 2 && onCompare && (
                    <Button
                        size="small"
                        startIcon={<CompareArrowsIcon />}
                        onClick={() => onCompare(selectedForCompare)}
                        sx={isRetro ? {
                            color: vortxColors.gold,
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            borderRadius: 0,
                            border: `1px solid ${vortxColors.gold}`,
                            boxShadow: `0 0 10px rgba(178, 140, 80, 0.3)`,
                            '&:hover': {
                                bgcolor: 'rgba(178, 140, 80, 0.1)',
                                boxShadow: `0 0 15px rgba(178, 140, 80, 0.5)`,
                            }
                        } : {
                            color: '#fff',
                            bgcolor: vortxColors.teal,
                            fontFamily: vortxTypography.body,
                            textTransform: 'none',
                            '&:hover': { bgcolor: alpha(vortxColors.teal, 0.8) }
                        }}
                    >
                        Compare ({selectedCount})
                    </Button>
                )}
            </Box>
        </>
    );
}
